// src/components/Study/Exam/QuestionListDrawer.tsx
import React from 'react';
import { X, Check } from 'react-feather';
import { Vignette } from '../../../../types/Vignette';
import { Block } from '../../../../types/Study';
import StandardFlag from '../../../../assets/images/standard/flag.svg';
import FlagDark from '../../../../assets/images/standard/flag-dark.svg';
import Pencil from '../../../../assets/images/standard/pencil.svg';
import { useSelector } from 'react-redux';
import { selectAccessLevel } from '../../../../slices/subscriptions/subscriptionsSlice';
import { getAccessLimits } from '../../../../utils/subAccessUtils';
import { Lock } from '../../../../assets/svgs/Lock';
import { selectTodaySession } from '../../../../slices/todaySession/todaySessionSlice';
import { useAccess } from '../../../../hooks/useAccess';

interface QuestionListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  vignettes: Vignette[];
  activeQuestionIndex: number;
  activeBlock: Block | undefined;
  onQuestionSelect: (index: number) => void;
  isAnswered: (vignetteId: string) => boolean;
  setUpgradeModalIsOpen: (isOpen: boolean) => void;
  isBlockSubmitted: boolean;
}

const QuestionListDrawer: React.FC<QuestionListDrawerProps> = ({
  isOpen,
  onClose,
  vignettes,
  activeQuestionIndex,
  activeBlock,
  onQuestionSelect,
  isAnswered,
  setUpgradeModalIsOpen,
  isBlockSubmitted,
}) => {
  const todaySession = useSelector(selectTodaySession);
  const accessLevel = useSelector(selectAccessLevel);
  const qbankLimit = getAccessLimits(accessLevel)?.qbankDaily;
  const { isGated } = useAccess();

  if (!activeBlock) {
    return null;
  }

  return (
    <div className={`questions ${isOpen ? 'show' : ''}`}>
      <div className="questions_header">
        <h3>Select a Question</h3>
        <button
          className="button button--icon-only"
          aria-label="Close Question List"
          onClick={onClose}
        >
          <X />
        </button>
      </div>
      <div className="questions_list">
        {vignettes.map((v, i) => {
          const vignetteId = v.id;
          const answerData = activeBlock.answers?.[vignetteId];
          const storedAnswer = answerData?.answer;
          const isCorrect =
            storedAnswer && storedAnswer === answerData?.correctAnswer;
          const isSubmittedOrReviewed =
            activeBlock.isSubmitted || answerData?.submitted;
          const showCheckmark = isCorrect && isSubmittedOrReviewed;
          const isDisabled = qbankLimit !== undefined && i >= qbankLimit;

          return (
            <button
              className={`item ${i === activeQuestionIndex ? 'active' : ''} ${isAnswered(vignetteId) ? 'answered' : ''} ${isDisabled ? 'disabled' : ''}`}
              key={'q-' + vignetteId}
              value={i}
              onClick={() => {
                if (!isDisabled) {
                  onQuestionSelect(i);
                } else {
                  setUpgradeModalIsOpen(true);
                }
              }}
              // disabled={isDisabled}
            >
              <li>
                {showCheckmark && <Check />}
                {isDisabled && <Lock />}
                <span>{i + 1}</span>
                <div>
                  {activeBlock.flags?.[vignetteId] && (
                    <>
                      <img className="light" src={StandardFlag} alt="Flag" />
                      <img className="dark" src={FlagDark} alt="Flag" />
                    </>
                  )}
                  {activeBlock.notes?.[vignetteId] && (
                    <img className="pencil" src={Pencil} alt="Pencil" />
                  )}
                </div>
              </li>
            </button>
          );
        })}
        {isGated() && !todaySession?.isFirstSession && !isBlockSubmitted && (
          <div
            className="questions_list_empty"
            onClick={() => {
              setUpgradeModalIsOpen(true);
            }}
          >
            {Array(40 - vignettes.length)
              .fill(null)
              .map((_, i) => {
                return (
                  <button
                    className={`item`}
                    key={'q-' + i}
                    value={i}
                    onClick={() => {
                      setUpgradeModalIsOpen(true);
                    }}
                  >
                    <li>
                      <span>{i + vignettes.length + 1}</span>
                    </li>
                  </button>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionListDrawer;
